import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import RightArrowIcon from 'transfix-icons/build/RightArrowIcon'
import { List, ListSubheader, ListItem, Typography } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { calcRem } from 'transfix-ui/theme/typography'

const useNavListMobileStyles = makeStyles(theme => ({
  list: {
    padding: `${theme.spacing(3)} 0 0`,
  },
  subheader: {
    color: brandColors.coolGray5,
    fontSize: calcRem(12),
    lineHeight: calcRem(16),
    letterSpacing: 1.5,
    paddingTop: 0,
    paddingBottom: 12,
    textTransform: 'uppercase',
  },
  item: {
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    backgroundColor: brandColors.white,
    color: brandColors.black,
    '&.MuiListItem-divider': {
      borderColor: brandColors.coolGray1,
    },
  },
  itemLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  icon: {
    color: brandColors.coolGray5,
  },
  errorColors: {
    color: brandColors.error1,
  },
}))

interface IListItem {
  /**
   * Text that will be shown to the user
   */
  label: string

  /**
   * Path to navigate to, when the item is clicked
   */
  path: string
}

interface INavListMobileProps {
  /**
   * Title of the group of items
   * @default ''
   */
  listTitle?: string
  /**
   * Navigation items to list on screen
   */
  items: IListItem[]
  /**
   * Use brandColors.error1 color for text and icon
   */
  useErrorColors?: boolean
}

export const NavListMobile: FC<INavListMobileProps> = ({
  listTitle = '',
  items = [],
  useErrorColors = false,
}) => {
  const classes = useNavListMobileStyles()
  const listItemClass = classNames(classes.item, {
    [classes.errorColors]: useErrorColors,
  })
  const iconClass = classNames(classes.icon, {
    [classes.errorColors]: useErrorColors,
  })

  return (
    <List
      className={classes.list}
      subheader={
        listTitle ? (
          <ListSubheader className={classes.subheader}>{listTitle}</ListSubheader>
        ) : undefined
      }>
      {items.map(({ label, path }, index, { length }) => (
        <ListItem
          key={path}
          component={Link}
          className={listItemClass}
          to={path}
          divider={index !== length - 1} // show the bottom border unless it's the last item
        >
          <div className={classes.itemLabel}>
            <Typography variant='body2' component='div'>
              {label}
            </Typography>
            <RightArrowIcon className={iconClass} />
          </div>
        </ListItem>
      ))}
    </List>
  )
}
